import React, { useEffect, useCallback, useRef, useImperativeHandle, forwardRef } from 'react';
import FilterDropdown from './FilterDropdown';
import CompanyFilter from './CompanyFilter';
import TextFilter from './TextFilter';
import ExportButton from './ExportButton';
import QuickSaveButton from './QuickSaveButton';
import SavedSearchesButton from './SavedSearchesButton';
import HelpButton from './HelpButton';

/**
 * Bottom sheet that holds every filter (and the search tools) on phones,
 * where the desktop toolbar row does not fit (see #311).
 *
 * The filters are the same FilterDropdown / CompanyFilter / TextFilter
 * components used in the desktop header, rendered in `inline` (accordion)
 * mode so their menus flow inside the sheet instead of popping over it.
 *
 * The ref exposes `measure()`, the sheet panel's bounding rect (or null while
 * closed), so the parent can keep the map framing clear of the sheet.
 */
const MobileFiltersSheet = forwardRef(function MobileFiltersSheet({
  isOpen,
  onClose,
  filterOptions,
  companies,
  activeFilters,
  onFilterChange,
  onTitleChange,
  onClearAll,
  resultCount,
  // Search tools (Export / Quick Save / My Searches / Help). `onRequestTool`
  // receives one of 'export' | 'quickSave' | 'savedSearches' | 'help'; the
  // parent runs the matching action after the filter state has committed.
  showTools = true,
  isSubscriber = false,
  onRequestTool,
  quickSaveLoading = false,
  savedSearchCount = 0,
}, ref) {
  const sheetRef = useRef(null);
  const textFilterRef = useRef(null);

  useImperativeHandle(ref, () => ({
    measure: () => (sheetRef.current ? sheetRef.current.getBoundingClientRect() : null),
  }), []);

  // Push any debounced title text through before the sheet goes away so the
  // parent acts on the text the user actually typed. The parent is told the
  // sheet is closing first so the resulting map re-fit uses the normal
  // framing rather than the sheet-aware one.
  const flushAndClose = useCallback(() => {
    onClose();
    if (textFilterRef.current) textFilterRef.current.flush();
  }, [onClose]);

  // Drop any debounced title text before the parent resets the filters, so
  // it can't fire afterwards and re-apply a search the user just cleared.
  const handleClearAll = useCallback(() => {
    if (textFilterRef.current) textFilterRef.current.clear();
    onClearAll();
  }, [onClearAll]);

  // Close on Escape, unless it was pressed in a field inside the sheet (e.g.
  // the Company search), where the field handles it (dismiss suggestions).
  useEffect(() => {
    if (!isOpen) return undefined;
    const handleKey = (e) => {
      if (e.key !== 'Escape') return;
      const target = e.target;
      const inSheetField =
        target instanceof Element &&
        (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA') &&
        sheetRef.current &&
        sheetRef.current.contains(target);
      if (inSheetField) return;
      flushAndClose();
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [isOpen, flushAndClose]);

  // Lock page scroll behind the sheet while it is open.
  useEffect(() => {
    if (!isOpen) return undefined;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, [isOpen]);

  const handleBackdropClick = useCallback((e) => {
    if (sheetRef.current && !sheetRef.current.contains(e.target)) {
      flushAndClose();
    }
  }, [flushAndClose]);

  // Close the sheet before handing off to a tool so its modal / ribbon
  // message is not stacked on top of (or hidden behind) the sheet. The
  // title filter is flushed first and the parent dispatches the tool from
  // an effect, so it sees the committed filter state rather than the
  // closure from this render.
  const runTool = useCallback((tool) => () => {
    flushAndClose();
    if (typeof onRequestTool === 'function') onRequestTool(tool);
  }, [flushAndClose, onRequestTool]);

  if (!isOpen) return null;

  const hasAnyFilter =
    activeFilters.uses.length > 0 ||
    activeFilters.status.length > 0 ||
    activeFilters.neighborhood.length > 0 ||
    activeFilters.company.length > 0 ||
    (activeFilters.title || '').trim() !== '';

  return (
    <div className="map-filters-sheet-backdrop" onClick={handleBackdropClick}>
      <div
        className="map-filters-sheet"
        ref={sheetRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="map-filters-sheet-title"
      >
        <div className="map-filters-sheet__header">
          <h2 id="map-filters-sheet-title" className="map-filters-sheet__title">Filters</h2>
          <button
            type="button"
            className="map-filters-sheet__close"
            onClick={flushAndClose}
            aria-label="Close filters"
          >
            <svg width="18" height="18" viewBox="0 0 20 20" fill="none" aria-hidden="true">
              <path d="M3 3L17 17M3 17L17 3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </svg>
          </button>
        </div>

        <div className="map-filters-sheet__body">
          <div className="map-filters-sheet__search">
            <TextFilter
              ref={textFilterRef}
              label="Search by title"
              value={activeFilters.title}
              onChange={onTitleChange}
              placeholder="Project Name"
            />
          </div>

          <FilterDropdown
            inline
            label="Land Use"
            options={filterOptions.uses}
            selected={activeFilters.uses}
            onChange={(values) => onFilterChange('uses', values)}
          />
          <FilterDropdown
            inline
            label="Status"
            options={filterOptions.status}
            selected={activeFilters.status}
            onChange={(values) => onFilterChange('status', values)}
          />
          <FilterDropdown
            inline
            label="Neighborhood"
            options={filterOptions.neighborhood}
            selected={activeFilters.neighborhood}
            onChange={(values) => onFilterChange('neighborhood', values)}
          />
          <CompanyFilter
            inline
            companies={companies}
            selected={activeFilters.company}
            onChange={(values) => onFilterChange('company', values)}
          />

          {showTools && (
            <div className="map-filters-sheet__tools" role="group" aria-label="Search tools">
              <ExportButton
                onClick={runTool('export')}
                isSubscriber={isSubscriber}
                label="Export data"
              />
              <QuickSaveButton
                onClick={runTool('quickSave')}
                loading={quickSaveLoading}
                isSubscriber={isSubscriber}
                label="Save search"
              />
              <SavedSearchesButton
                onClick={runTool('savedSearches')}
                count={savedSearchCount}
                isSubscriber={isSubscriber}
              />
              <HelpButton onClick={runTool('help')} label="Help" />
            </div>
          )}
        </div>

        <div className="map-filters-sheet__footer">
          <button
            type="button"
            className="map-filters-sheet__clear"
            onClick={handleClearAll}
            disabled={!hasAnyFilter}
          >
            Clear all
          </button>
          <button
            type="button"
            className="map-filters-sheet__apply"
            onClick={flushAndClose}
          >
            Show {resultCount} project{resultCount === 1 ? '' : 's'}
          </button>
        </div>
      </div>
    </div>
  );
});

export default MobileFiltersSheet;
